<script lang="ts">
    import { openModal } from 'svelte-modals';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';

    import { debug, deliveryList, planning, scenarioManager } from '../../../../stores/store';
    import IconPlanningStep3 from '../../../shared/Icons/IconPlanningStep3.svelte';
    import ModalSelectRoute from '../../Modals/ModalSelectRoute.svelte';
    import ModalSetTargetDate from '../../Modals/ModalSetTargetDate.svelte';
    import { DeliveriesBulkActions } from './DeliveriesBulkActions';
    import ModalConfirm from '../../../shared/Modals/ModalConfirm.svelte';
    import IconVehicle from '../../../shared/Icons/IconVehicle.svelte';
    import IconRemove from '../../../shared/Icons/IconRemove.svelte';
    import IconClose from '../../../shared/Icons/IconClose.svelte';
    import IconCalendar from '../../../shared/Icons/IconCalendar.svelte';
    import Spinner from '../../../shared/Spinner/Spinner.svelte';
    import { api } from '../../../../stores/api';
    import ModalError from '../../../shared/Modals/ModalError.svelte';
    import { DeliveriesMenuActions } from './DeliveriesMenuActions';
    import { BulkAction } from '../BulkAction';
    import BulkActionOne from '../BulkActionOne.svelte';
    import type { AssignmentJob } from '../AssignmentJob';
    import { JobApi } from '../../Jobs/JobApi';
    import { jobs } from '../../../../stores/store';
    import { Umami } from '../../../utilities/Umami';

    export let disabled: boolean = false;
    export let selectedItems: AssignmentJob[] = [];
    export let onAction: () => void = () => {};
    export let onClose: (e: any) => void = () => {};

    let selectedCount: number = 0;
    let inProgress: boolean = false;

    let assignQueue: Record<string,number[]> = {};
    let totalJobsToAssign: number = 0;

    let actions: Record<string, BulkAction> = {
        'assign': new BulkAction(
            IconPlanningStep3,
            $_('Accept'),
            (items: any[]) => {
                console.log('MonitActionForAssignmentJobs, setAssign', items);
                setAssign(items);
            },
            true
        ),
        'reject': new BulkAction(
            IconRemove,
            $_('Reject'),
            (items: any[]) => {
                removeAssign(items);
            },
            true
        ),
        'removeFromRoute': new BulkAction(
            IconRemove,
            $_('Remove from route'),
            (items: any[]) => {
                removeAssign(items);
            },
            true
        ),
        'selectVehicle': new BulkAction(
            IconVehicle,
            $_('Select vehicle'),
            (items: any[]) => {
                selectVehicle(items);
            },
            true
        ),
        'changeTargetDate': new BulkAction(
            IconCalendar,
            $_('change_target_date'),
            (items: any[]) => {
                changeTargetDate(items);
            },
            true
        )
    }

    function isJobAvailableForAction(action: string, aj: AssignmentJob|null): boolean {
        if (aj) {
            switch (action) {
                case 'assign':
                    return aj.hasSuggestedAssignment() && DeliveriesMenuActions.deliveryCanBeAssigned(aj, $scenarioManager.getAssignmentById(aj.getSuggestedAssignmentId()));
                case 'reject':
                    return aj.hasSuggestedAssignment() && DeliveriesMenuActions.deliveryCanBeRejected(aj, $scenarioManager.getAssignmentById(aj.getSuggestedAssignmentId()));
                case 'removeFromRoute':
                    return aj.getAssignmentId() > 0 && DeliveriesMenuActions.hasAssignment(aj);
                case 'selectVehicle':
                    return DeliveriesMenuActions.deliveryCanBeAssignedToAnotherVehicle(aj, $scenarioManager.getAssignmentById(aj.getAssignmentId() || 0));
                case 'changeTargetDate':
                    return DeliveriesMenuActions.deliveryCanBeAssignedToAnotherVehicle(aj, $scenarioManager.getAssignmentById(aj.getAssignmentId() || 0));
            }
        }
        return false;
    }

    function doAssign(assignment_id: number, jobsToProcess: number[]) {
        if (disabled) {
            console.warn('disabled, cannot doAssign');
            return;
        }
        inProgress = true;
        DeliveriesBulkActions.doAssign(
            $api,
            assignment_id,
            jobsToProcess,
            'selected',
            {},
            $planning.selectedDay
        )
            .then(_ => {
                /* todo */
            })
            .catch(err => {
                Umami.error('planning_assign_jobs', err, { assignment_id });
                console.error('err', err);
                openModal(ModalError, {
                    error: err,
                    header: '',
                    message: $_(err),
                });
            })
            .finally(() => {
                console.log('MonitActionForAssignmentJobs, doAssign, finally', Object.keys(assignQueue));
                if (Object.keys(assignQueue).length === 0) {
                    Umami.event('planning_assign_jobs', { count: totalJobsToAssign });
                    onAction();
                    inProgress = false;
                } else {
                    processAssignQueue();
                }
            });
    }

    function setAssign(jobsToProcess: AssignmentJob[]) {
        if (disabled) {
            console.warn('disabled, cannot setAssign');
            return;
        }
        inProgress = true;
        assignQueue = {};
        totalJobsToAssign = jobsToProcess.length;
        for (let i = 0; i < jobsToProcess.length; i++) {
            if (jobsToProcess[i].getSuggestedAssignmentId() !== null) {
                if (!assignQueue[jobsToProcess[i].getSuggestedAssignmentId()]) {
                    assignQueue[jobsToProcess[i].getSuggestedAssignmentId()] = [];
                }
                assignQueue[jobsToProcess[i].getSuggestedAssignmentId()].push(jobsToProcess[i].getJobId());
            }
        }
        processAssignQueue();
    }

    function processAssignQueue() {
        console.log('MonitActionForAssignmentJobs, processAssignQueue', Object.keys(assignQueue), assignQueue);
        let assignmentIds: number[] = Object.keys(assignQueue).map(parseInt);
        if (assignmentIds.length > 0) {
            let targetAssignmentId = assignmentIds.shift();
            if (targetAssignmentId) {
                let jobsToAssign: number[] = assignQueue[targetAssignmentId.toString()];
                delete(assignQueue[targetAssignmentId]);
                doAssign(targetAssignmentId, jobsToAssign);
            }
        }
    }

    function removeAssign(jobsToProcess: AssignmentJob[]) {
        if (disabled) {
            console.warn('disabled, cannot removeAssign');
            return;
        }
        inProgress = true;
        DeliveriesBulkActions.doReject(
            $api,
            jobsToProcess.map(aj => aj.getJobId()),
            'selected',
            {},
            null,
            $planning.selectedDay
        )
            .then(_ => {
                Umami.event('job_operator_reject', { count: jobsToProcess.length });
                onAction();
            })
            .catch(err => {
                Umami.error('job_operator_reject', err, { count: jobsToProcess.length });
                console.error('err', err);
                openModal(ModalError, {
                    error: err,
                    header: '',
                    message: $_(err),
                });
            })
            .finally(() => {
                inProgress = false;
            });
    }

    function selectVehicle(jobsToProcess: AssignmentJob[]) {
        if (disabled) {
            console.warn('disabled, cannot selectVehicle');
            return;
        }
        openModal(ModalSelectRoute, {
            onClick: (assignmentId, assignment) => {
                return new Promise((resolve, reject) => {
                    if (assignmentId !== null) {
                        let assignment = $scenarioManager.getAssignmentById(assignmentId);
                        if (assignment?.isRouteCalculated()) {
                            let confirm: { title: string; message: string };
                            if (assignment.isLocked()) {
                                if (assignment.isDownloaded()) {
                                    confirm = {
                                        title: $_('route_already_downloaded'),
                                        message: $_(
                                            'new_jobs_will_be_added_on_commit_subject_to_approval'
                                        ),
                                    };
                                } else {
                                    confirm = {
                                        title: $_('route_already_locked'),
                                        message: $_('adding_jobs_unlocks_route'),
                                    };
                                }
                            } else {
                                confirm = {
                                    title: $_('assignment_has_route_calculated'),
                                    message: $_('assignment_route_will_be_removed_on_assign'),
                                };
                            }
                            openModal(ModalConfirm, {
                                title: confirm.title,
                                text: confirm.message,
                                onClick: () => {
                                    doAssign(assignmentId, jobsToProcess.map(aj => aj.getJobId()));
                                    resolve(true);
                                },
                                onCancel: () => {
                                    resolve(false);
                                },
                            });
                            return;
                        } else {
                            doAssign(assignmentId, jobsToProcess.map(aj => aj.getJobId()));
                            resolve(true);
                        }
                    } else {
                        resolve(true);
                    }
                });
            },
            selectedScenarioId: $scenarioManager.getScenarioId(),
            vehicles: $scenarioManager.getVehiclesData(),
            inProgressSwitch: false,
            onlyLocked: false,
        });
    }

    function changeTargetDate(jobsToProcess: AssignmentJob[]) {
        if (disabled) {
            console.warn('disabled, cannot removeAssign');
            return;
        }
        let jobIdsToProcess = jobsToProcess.map(aj => aj.getJobId());
        openModal(ModalSetTargetDate, {
            text: $_(`Select target date for selected jobs`) + ` (${selectedCount})`,
            onClick: () => {
                inProgress = true;
                DeliveriesBulkActions.doReject(
                    $api,
                    jobIdsToProcess,
                    'selected',
                    {},
                    null,
                    $planning.selectedDay
                )
                    .then(_ => {
                        return JobApi.modify(
                            get(api),
                            { id: { $in: jobIdsToProcess } },
                            { target_date: $jobs.defaultTargetDate }
                        )
                    })
                        .then(_ => {
                            onAction();
                        })
                        .catch(err => {
                            console.error('err', err);
                            openModal(ModalError, {
                                error: err,
                                header: '',
                                message: $_(err),
                            });
                        })
                        .finally(() => {
                            inProgress = false;
                        });
            }
        });
    }

    $: {

        console.log('onlySelectedFields BB', selectedItems.length);

        selectedCount = selectedItems.length;

        for (let a in actions) {
            actions[a] = actions[a]
                .setItems(
                    selectedItems.filter(aj => {
                        try {
                            return isJobAvailableForAction(a, aj);
                        } catch (e) {
                            return false;
                        }
                    })
                );
        }

    }
</script>

<div class="lm--action_bar" style="display: flex;">
    {#if inProgress}
        <Spinner />
    {:else}
        <div>
            <span class="text-span-10">{$_('Selected items')}</span>:
            <strong>{selectedCount}</strong>
        </div>
        <div class="lm--action_bar_loptions" class:disabled>
            {#each Object.values(actions) as action}
                <BulkActionOne {action}/>
            {/each}
        </div>
        <IconClose
            scale={0.7}
            on:click={onClose}
        />
    {/if}
</div>

<style>
    .lm--action_bar {
        font-size: 14px;
        z-index: 1;
        min-height: 72px;
        color: #fff;
        background-color: #397be5;
        justify-content: space-between;
        align-items: center;
        padding: 10px 30px;
        display: none;
        position: absolute;
        column-gap: 20px;
        top: 0;
        bottom: auto;
        left: 0;
        right: 0;
    }
    .lm--action_bar_loptions {
        grid-column-gap: 30px;
        grid-row-gap: 20px;
        /*filter: invert();*/
        color: #000;
        align-items: center;
        font-weight: 500;
        line-height: 14px;
        display: flex;
        flex-wrap: wrap;
    }
    .disabled {
        opacity: 0.5;
        pointer-events: none;
    }
</style>
